import {
  Badge,
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useCallback, useEffect, useState } from 'react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ServerError } from '@texas/types';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { levenshteinDistance } from '@texas/utils/helpers/levenshtein';
import { reactEvents } from '@bridge/reactEvents';
import { useTranslation } from 'react-i18next';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';

export interface LinkOptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  variantId: number;
  productDevelopmentVariantId: number;
  currentlyLinkedOptionId?: number;
  onLinked: () => void;
  variantName?: string;
}

export function LinkOptionModal(props: LinkOptionModalProps) {
  const toast = useToast();
  const { t } = useTranslation();

  const [pendingOptionId, setPendingOptionId] = useState<number | null>(null);

  const {
    data: options,
    loading,
    error,
    refetch,
  } = useApiResource(compositionGroupApi.optionsOverview);

  const {
    request: linkRequest,
    loading: linkLoading,
    error: linkError,
  } = useApiRequest(variantsApi.linkVariantToOption);

  const getRecommendedOptionId = useCallback((): number | undefined => {
    if (!options || !props.variantName) return undefined;

    let minDistance = Infinity;
    let recommendedId: number | undefined = undefined;

    options.forEach((option) => {
      const optionLabel = option.note
        ? option.note
        : t('compositionGroup.option.optionNr', { nr: option.version });

      const distance = levenshteinDistance(props.variantName!, optionLabel);
      if (distance < minDistance) {
        minDistance = distance;
        recommendedId = option.id;
      }
    });

    return recommendedId;
  }, [options, props.variantName, t]);

  const recommendedOptionId = getRecommendedOptionId();

  useEffect(() => {
    if (props.isOpen) {
      refetch({ variantId: props.productDevelopmentVariantId });
    }
  }, [props.isOpen, props.productDevelopmentVariantId, refetch]);

  const handleLinkOption = async (compositionGroupId: number) => {
    await request(
      linkRequest,
      [props.variantId, compositionGroupId],
      (variantName: string) => {
        toast({
          title: t('garp.linkedSuccessfully'),
          status: 'success',
          isClosable: true,
        });
        reactEvents.variantNameChanged.dispatch({
          id: props.variantId,
          value: variantName,
        });
        reactEvents.variantOptionLinked.dispatch();
        props.onLinked();
        props.onClose();
      },
      (error: ServerError) => {
        toast({
          title: t('garp.failedToLinkOption'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex flexDir="column" gap={1}>
            <Heading size="md">{t('garp.linkOption')}</Heading>
            {props.variantName && (
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                {t('garp.variantLabel', { name: props.variantName })}
              </Text>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Heading fontSize="md" pb={2}>
            {t('garp.selectYourProductDevelopmentOption')}
          </Heading>
          {linkError && <ErrorDetails error={linkError} />}
          {error && <ErrorDetails error={error} />}
          {loading ? (
            <Flex justifyContent="center" py={8}>
              <Spinner size="lg" />
            </Flex>
          ) : options && options.length > 0 ? (
            <Flex flexDir="column" gap={2}>
              {options.map((option) => {
                const isCurrentlyLinked =
                  props.currentlyLinkedOptionId === option.id;
                const isLinkedToOther =
                  option.linkedVariantId &&
                  option.linkedVariantId !== props.variantId;
                const isRecommended = recommendedOptionId === option.id;

                return (
                  <Box
                    key={option.id}
                    p={4}
                    borderRadius="md"
                    border="2px solid"
                    borderColor={isCurrentlyLinked ? 'blue.400' : 'gray.200'}
                    bg={isCurrentlyLinked ? 'blue.50' : 'white'}
                    cursor="pointer"
                    _hover={{ bg: isCurrentlyLinked ? 'blue.100' : 'gray.50' }}
                    onClick={() => {
                      if (isLinkedToOther) {
                        setPendingOptionId(option.id);
                      } else {
                        handleLinkOption(option.id);
                      }
                    }}
                    opacity={isLinkedToOther ? 0.5 : 1}
                    pointerEvents={linkLoading ? 'none' : 'auto'}
                  >
                    <Flex
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={2}
                    >
                      <Box flex={1}>
                        <Flex alignItems="center" gap={2} mb={1}>
                          <CompositionGroupTitle
                            version={option.version}
                            note={option.note}
                            type="option"
                          />
                        </Flex>
                        {option.supplierName && (
                          <Text fontSize="sm" color="gray.600">
                            {option.supplierName}
                          </Text>
                        )}
                        {isLinkedToOther && (
                          <Badge colorScheme="orange" mt={2}>
                            {t('garp.alreadyLinkedTo', {
                              name: option.linkedVariantName,
                            })}
                          </Badge>
                        )}
                      </Box>
                      <Flex flexDir="column" gap={1} alignItems="flex-end">
                        {isCurrentlyLinked && (
                          <Badge colorScheme="blue">{t('garp.current')}</Badge>
                        )}
                        {isRecommended && !isCurrentlyLinked && (
                          <Badge colorScheme="purple">
                            {t('garp.bestMatch')}
                          </Badge>
                        )}
                        {option.isReady && (
                          <Badge colorScheme="green">{t('garp.ready')}</Badge>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          ) : (
            <Text color="gray.500">{t('garp.noOptionsAvailable')}</Text>
          )}
        </ModalBody>
      </ModalContent>

      <VerifyDialogWithFunction
        isOpen={pendingOptionId !== null}
        onClose={() => setPendingOptionId(null)}
        headerTitle={t('garp.alreadyLinkedTitle')}
        primaryButtonTitle={t('garp.alreadyLinkedConfirm')}
        secondaryButtonTitle={t('general.cancel')}
        toPerform={() => handleLinkOption(pendingOptionId!)}
      >
        {t('garp.alreadyLinkedBody')}
      </VerifyDialogWithFunction>
    </Modal>
  );
}
